import type { Answers, FieldDef } from '../api/types'
import { groupFields } from '../lib/answers'

interface Props {
  fields: FieldDef[]
  values: Answers
  errors: Record<string, string>
  onChange: (key: string, value: string | string[] | boolean) => void
  /** Prefix for element ids, so two forms on a page never share them. */
  idPrefix?: string
}

/** Renders the campaign's own questions, grouped under their headings, with the error for each shown beside it. */
export default function DynamicFields({ fields, values, errors, onChange, idPrefix = 'f' }: Props) {
  return (
    <>
      {groupFields(fields).map((g) => (
        <section key={g.group || 'main'} className="card">
          {g.group && <h2>{g.group}</h2>}
          {g.fields.map((f) => <Question key={f.key} f={f} id={`${idPrefix}-${f.key}`} value={values[f.key]} error={errors[f.key]} onChange={(v) => onChange(f.key, v)} />)}
        </section>
      ))}
    </>
  )
}

type Value = string | string[] | boolean | undefined

function Question({ f, id, value, error, onChange }: { f: FieldDef; id: string; value: Value; error?: string; onChange: (v: string | string[] | boolean) => void }) {
  const describedBy = [f.helpText ? `${id}-help` : null, error ? `${id}-err` : null].filter(Boolean).join(' ') || undefined
  const common = { id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined }
  const text = typeof value === 'string' ? value : ''
  const help = f.helpText ? <p id={`${id}-help`} className="hint">{f.helpText}</p> : null
  const err = error ? <p id={`${id}-err`} role="alert" className="field-error">{error}</p> : null

  if (f.type === 'MULTI_SELECT') {
    const chosen = Array.isArray(value) ? value : []
    return (
      <fieldset className="question-set" aria-describedby={describedBy}>
        <legend>{f.label}{f.required ? '' : ' (optional)'}</legend>
        {help}
        {f.options.map((o) => (
          <label key={o} className="check">
            <input type="checkbox" checked={chosen.includes(o)} onChange={(e) => onChange(e.target.checked ? [...chosen, o] : chosen.filter((x) => x !== o))} />
            <span>{o}</span>
          </label>
        ))}
        {err}
      </fieldset>
    )
  }
  if (f.type === 'CHECKBOX') {
    return (
      <div className="field-block">
        <label className="check">
          <input {...common} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          <span>{f.label}</span>
        </label>
        {help}
        {err}
      </div>
    )
  }

  let input
  switch (f.type) {
    case 'LONG_TEXT':
      input = <textarea {...common} rows={4} value={text} onChange={(e) => onChange(e.target.value)} />
      break
    case 'SELECT':
      input = (
        <select {...common} value={text} onChange={(e) => onChange(e.target.value)}>
          <option value="" disabled>Select…</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      )
      break
    case 'NUMBER':
      input = <input {...common} type="text" inputMode="decimal" value={text} onChange={(e) => onChange(e.target.value)} />
      break
    case 'DATE':
      input = <input {...common} type="date" value={text} onChange={(e) => onChange(e.target.value)} />
      break
    case 'PHONE':
      input = <input {...common} type="tel" inputMode="tel" autoComplete="tel" value={text} onChange={(e) => onChange(e.target.value)} />
      break
    case 'EMAIL':
      input = <input {...common} type="email" autoComplete="email" value={text} onChange={(e) => onChange(e.target.value)} />
      break
    default:
      input = <input {...common} type="text" value={text} onChange={(e) => onChange(e.target.value)} />
  }
  return (
    <div className="field-block">
      <label htmlFor={id} className="field-label">{f.label}{f.required ? '' : ' (optional)'}</label>
      {help}
      {input}
      {err}
    </div>
  )
}
